import * as XLSX from "xlsx"
import type { MovementControlRow } from "../types/inventoryMovement"
import {
  computeMovementKpi,
  formatNumberId
} from "./inventoryMovementHelpers"

export const MOVEMENT_EXPORT_HEADERS = [
  "No",
  "Date",
  "Transaction",
  "Reference",
  "Location",
  "Opening",
  "IN",
  "OUT",
  "Expected Closing",
  "Actual Closing",
  "Variance",
  "Cost",
  "Value",
  "Status",
  "Control Note",
  "Created By"
]

export function buildMovementExportRows(
  rows: MovementControlRow[]
): (string | number)[][] {
  return rows.map((row, index) => [
    index + 1,
    row.date,
    row.transactionType,
    row.reference,
    row.location,
    row.opening,
    row.stockIn,
    row.stockOut,
    row.expectedClosing,
    row.actualClosing,
    row.variance,
    row.cost,
    row.value,
    row.status,
    row.controlNote,
    row.createdBy
  ])
}

export function exportMovementXlsx(params: {
  periodLabel: string
  rows: MovementControlRow[]
  fileName?: string
}) {
  const { periodLabel, rows, fileName } = params
  const kpi = computeMovementKpi(rows)
  const body = buildMovementExportRows(rows)

  const worksheetData: (string | number)[][] = [
    ["Inventory Report"],
    [
      `Inventory Movement / Stock Card Control Report | Period: ${periodLabel}`
    ],
    ["Formula: Previous Balance + IN - OUT = Closing Balance"],
    [],
    [
      "Opening Balance",
      "Total In",
      "Total Out",
      "Closing Balance",
      "OK Records",
      "Warning",
      "Error",
      "Control Score"
    ],
    [
      formatNumberId(kpi.openingBalance),
      formatNumberId(kpi.totalIn),
      formatNumberId(kpi.totalOut),
      formatNumberId(kpi.closingBalance),
      String(kpi.okCount),
      String(kpi.warningCount),
      String(kpi.errorCount),
      `${kpi.controlScorePct}%`
    ],
    [],
    MOVEMENT_EXPORT_HEADERS,
    ...body
  ]

  const ws = XLSX.utils.aoa_to_sheet(worksheetData)
  ws["!cols"] = [
    { wch: 6 },
    { wch: 14 },
    { wch: 22 },
    { wch: 20 },
    { wch: 18 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
    { wch: 16 },
    { wch: 16 },
    { wch: 12 },
    { wch: 14 },
    { wch: 16 },
    { wch: 12 },
    { wch: 28 },
    { wch: 16 }
  ]

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, "Movement Control")
  XLSX.writeFile(
    wb,
    fileName ?? "Inventory_Movement_Control_Report.xlsx"
  )
}

export function printMovementHtml(params: {
  periodLabel: string
  rows: MovementControlRow[]
  orientation: "portrait" | "landscape"
}): Window | null {
  const { periodLabel, rows, orientation } = params
  const kpi = computeMovementKpi(rows)
  const headers = MOVEMENT_EXPORT_HEADERS
  const exportRows = buildMovementExportRows(rows)
  const pageSize = orientation === "landscape" ? "landscape" : "portrait"
  const fontSize = orientation === "landscape" ? "8px" : "6px"

  const w = window.open("", "_blank", "noopener,noreferrer")
  if (!w?.document) return null

  const html = `<!DOCTYPE html>
<html>
<head>
  <title>Inventory Movement Control Print</title>
  <style>
    @page { size: A4 ${pageSize}; margin: 8mm; }
    body { font-family: Arial, Helvetica, sans-serif; color: #111; }
    h1 { font-size: 20px; margin: 0; }
    .subtitle { font-size: 12px; margin: 4px 0 6px 0; }
    .formula { font-size: 10px; margin-bottom: 12px; }
    .kpi { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
    .kpi th { background: #050505; color: white; font-size: 8px; padding: 5px; border: 1px solid #999; }
    .kpi td { text-align: center; font-weight: 800; font-size: 12px; padding: 6px; border: 1px solid #999; }
    table.report { width: 100%; border-collapse: collapse; table-layout: fixed; }
    table.report th { background: #050505; color: white; padding: 3px; border: 1px solid #777; font-size: ${fontSize}; }
    table.report td { padding: 3px; border: 1px solid #999; font-size: ${fontSize}; word-wrap: break-word; }
    .right { text-align: right; }
  </style>
</head>
<body>
  <h1>Inventory Report</h1>
  <div class="subtitle">Inventory Movement / Stock Card Control Report | Period: ${periodLabel}</div>
  <div class="formula">Formula: Previous Balance + IN - OUT = Closing Balance</div>
  <table class="kpi">
    <tr><th>Opening</th><th>Total In</th><th>Total Out</th><th>Closing</th><th>OK</th><th>Warning</th><th>Error</th><th>Score</th></tr>
    <tr><td>${formatNumberId(kpi.openingBalance)}</td><td>${formatNumberId(kpi.totalIn)}</td><td>${formatNumberId(kpi.totalOut)}</td><td>${formatNumberId(kpi.closingBalance)}</td><td>${kpi.okCount}</td><td>${kpi.warningCount}</td><td>${kpi.errorCount}</td><td>${kpi.controlScorePct}%</td></tr>
  </table>
  <table class="report">
    <thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
    <tbody>
      ${exportRows
        .map(
          (row) =>
            `<tr>${row
              .map((cell, i) =>
                [5, 6, 7, 8, 9, 10, 11, 12].includes(i)
                  ? `<td class="right">${cell}</td>`
                  : `<td>${cell}</td>`
              )
              .join("")}</tr>`
        )
        .join("")}
    </tbody>
  </table>
</body>
</html>`

  w.document.open()
  w.document.write(html)
  w.document.close()
  w.focus()
  setTimeout(() => w.print(), 300)
  return w
}
